import {
  loadSupportIdentityVerifications,
  loadSupportPropertyVerifications,
  PropertyVerificationRecord,
  reviewIdentityVerification,
  reviewPropertyVerification,
  SupportReviewDocument,
  SupportReviewPhoto,
  SupportVerificationRecord,
} from 'api/services/verificationService'
import PageLoading from 'components/common/loading/PageLoading'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

type ReviewAction = 'approve' | 'reject' | 'request_correction'

const IDENTITY_PENDING = new Set([
  'identity_pending',
  'identity_in_review',
  'identity_correction_requested',
])

const PROPERTY_PENDING = new Set([
  'property_validation_pending',
  'property_docs_in_review',
  'property_correction_requested',
])

type OwnerListingItem = {
  key: string
  property: PropertyVerificationRecord | null
  identity: SupportVerificationRecord | null
  needsIdentity: boolean
  ownerFk: string
}

const statusBadgeClass = (status: string): string => {
  if (status.includes('approved') || status === 'active' || status === 'ready_to_publish') {
    return 'border-emerald-300 bg-emerald-100 text-emerald-900'
  }
  if (status.includes('rejected') || status === 'rejected') {
    return 'border-red-300 bg-red-100 text-red-900'
  }
  if (status.includes('correction')) {
    return 'border-amber-300 bg-amber-50 text-amber-900'
  }
  return 'border-blue-300 bg-blue-50 text-imovSecondary'
}

const QueueBadge = ({ status, label }: { status: string; label: string }) => (
  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(status)}`}>
    {label}
  </span>
)

const AccordionChevron = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="h-5 w-5 shrink-0 text-imovSecondary transition-transform duration-200 group-open:rotate-180"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
)

const documentLabel = (doc: SupportReviewDocument): string => {
  const name = (doc.file_name || '').trim()
  if (name) return name
  return doc.document_type
}

const ReviewDocuments = ({
  documents,
  openLabel,
  unavailableLabel,
  emptyLabel,
  allowOpen = true,
  title,
}: {
  documents?: SupportReviewDocument[]
  openLabel: string
  unavailableLabel: string
  emptyLabel: string
  allowOpen?: boolean
  title?: string
}) => {
  const docs = documents ?? []

  return (
    <div className={title ? 'mt-3 border-t border-gray-200 pt-3' : 'mt-3'}>
      {title ? <p className="mb-2 text-xs font-semibold text-gray-700">{title}</p> : null}
      {docs.length === 0 ? (
        <p className="text-xs text-gray-500">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1.5">
          {docs.map((doc) => {
            const url = (doc.file_url || '').trim()
            const label = documentLabel(doc)
            const canOpen = allowOpen && Boolean(url)
            return (
              <li
                key={doc.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-2.5 py-2 text-xs"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-gray-900">{label}</p>
                  <p className="truncate text-gray-500">
                    {doc.document_type}
                    {doc.version ? ` · v${doc.version}` : ''}
                  </p>
                </div>
                {canOpen ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded-lg border border-imovSecondary/20 bg-imovSecondary/5 px-2.5 py-1.5 font-semibold text-imovSecondary hover:bg-imovSecondary/10"
                  >
                    {openLabel}
                  </a>
                ) : (
                  <span className="shrink-0 text-gray-400">{unavailableLabel}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const ReviewPhotos = ({
  photos,
  title,
  openLabel,
  emptyLabel,
}: {
  photos?: SupportReviewPhoto[]
  title: string
  openLabel: string
  emptyLabel: string
}) => {
  const items = photos ?? []

  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <p className="mb-2 text-xs font-semibold text-gray-700">{title}</p>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500">{emptyLabel}</p>
      ) : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {items.map((photo) => (
            <li key={`${photo.id}-${photo.url}`} className="overflow-hidden rounded-md bg-white">
              <a
                href={photo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                title={photo.name || openLabel}
              >
                <img
                  src={photo.url}
                  alt={photo.name || openLabel}
                  className="h-24 w-full object-cover"
                />
                <span className="block px-2 py-1 text-[11px] font-semibold text-imovSecondary">
                  {openLabel}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const ReviewActions = ({
  disabled,
  onApprove,
  onRequestCorrection,
  onReject,
  approveLabel,
  requestCorrectionLabel,
  rejectLabel,
}: {
  disabled: boolean
  onApprove: () => void
  onRequestCorrection: () => void
  onReject: () => void
  approveLabel: string
  requestCorrectionLabel: string
  rejectLabel: string
}) => (
  <div className="mt-3 flex flex-wrap gap-2">
    <button
      type="button"
      onClick={onApprove}
      disabled={disabled}
      className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
    >
      {approveLabel}
    </button>
    <button
      type="button"
      onClick={onRequestCorrection}
      disabled={disabled}
      className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 disabled:opacity-50"
    >
      {requestCorrectionLabel}
    </button>
    <button
      type="button"
      onClick={onReject}
      disabled={disabled}
      className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-800 disabled:opacity-50"
    >
      {rejectLabel}
    </button>
  </div>
)

const isOwnerRole = (role?: string | null) => (role || 'tenant') === 'owner'

const isIdentityApproved = (identity: SupportVerificationRecord | null | undefined) =>
  Boolean(identity && identity.status === 'identity_approved')

const trimUserFk = (value?: string | null) => (value || '').trim()

const buildPropertyOwnerSet = (properties: PropertyVerificationRecord[]): Set<string> => {
  const owners = new Set<string>()
  properties.forEach((row) => {
    const fk = trimUserFk(row.responsible_user_fk)
    if (fk) owners.add(fk)
  })
  return owners
}

const buildOwnerListingQueue = (
  properties: PropertyVerificationRecord[],
  identities: SupportVerificationRecord[]
): OwnerListingItem[] => {
  const identityByUser = new Map<string, SupportVerificationRecord>()
  identities.forEach((row) => {
    const fk = trimUserFk(row.user_fk)
    if (!fk) return
    // Prefer the latest / pending record if duplicates ever appear.
    const current = identityByUser.get(fk)
    if (!current || row.id > current.id) {
      identityByUser.set(fk, row)
    }
  })

  const pendingProperties = properties.filter((row) => PROPERTY_PENDING.has(row.status))
  const usedIdentityIds = new Set<number>()

  const items: OwnerListingItem[] = pendingProperties.map((property) => {
    const ownerFk = trimUserFk(property.responsible_user_fk)
    const identity = identityByUser.get(ownerFk) ?? null
    if (identity) usedIdentityIds.add(identity.id)
    return {
      key: `property-${property.id}`,
      property,
      identity,
      needsIdentity: !isIdentityApproved(identity),
      ownerFk,
    }
  })

  // Owner who started identity but has no listing in the property queue yet.
  identities
    .filter((row) => {
      const fk = trimUserFk(row.user_fk)
      return (
        IDENTITY_PENDING.has(row.status) &&
        isOwnerRole(row.user_role) &&
        !usedIdentityIds.has(row.id) &&
        !pendingProperties.some((property) => trimUserFk(property.responsible_user_fk) === fk)
      )
    })
    .forEach((identity) => {
      items.push({
        key: `identity-${identity.id}`,
        property: null,
        identity,
        needsIdentity: true,
        ownerFk: trimUserFk(identity.user_fk),
      })
    })

  return items
}

const buildTenantQueue = (
  identities: SupportVerificationRecord[],
  properties: PropertyVerificationRecord[]
): SupportVerificationRecord[] => {
  const propertyOwners = buildPropertyOwnerSet(properties)
  return identities.filter((row) => {
    if (!IDENTITY_PENDING.has(row.status)) return false
    const fk = trimUserFk(row.user_fk)
    // Anyone tied to a listing verification is handled in the owner/listing queue.
    if (propertyOwners.has(fk)) return false
    // Explicit owners without a listing yet stay in the owner queue (orphan card).
    if (isOwnerRole(row.user_role)) return false
    // Staff test accounts should not appear as tenants.
    const role = row.user_role || 'tenant'
    if (role === 'support' || role === 'admin') return false
    return true
  })
}

const SupportConsole = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const identityQ = useQuery(['supportIdentityVerifications'], () =>
    loadSupportIdentityVerifications()
  )
  const propertyQ = useQuery(['supportPropertyVerifications'], () =>
    loadSupportPropertyVerifications()
  )

  const identityMutation = useMutation(reviewIdentityVerification, {
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : String(t('accessControl.supportConsole.reviewFailed'))
      )
    },
  })

  const propertyMutation = useMutation(reviewPropertyVerification, {
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : String(t('accessControl.supportConsole.reviewFailed'))
      )
    },
  })

  const identityStatusLabel = (status: string) =>
    t(`account.mySpace.identityStatus.${status}`, { defaultValue: status })
  const listingStatusLabel = (status: string) =>
    t(`account.listingManager.status.${status}`, { defaultValue: status })

  const refreshQueues = async () => {
    await Promise.all([
      queryClient.invalidateQueries(['supportIdentityVerifications']),
      queryClient.invalidateQueries(['supportPropertyVerifications']),
    ])
  }

  const reviewTenantIdentity = async (identityId: number, action: ReviewAction) => {
    try {
      await identityMutation.mutateAsync({ id: identityId, action })
      toast.success(String(t('accessControl.supportConsole.reviewSuccess')))
      await refreshQueues()
    } catch {
      // toast handled in mutation onError
    }
  }

  const reviewOwnerListing = async (
    item: OwnerListingItem,
    action: ReviewAction
  ) => {
    // Capture IDs at click-time so a shared identity cannot affect other cards.
    const identityId =
      item.needsIdentity && item.identity?.id ? item.identity.id : null
    const propertyId = item.property?.id ?? null

    try {
      if (identityId != null) {
        await identityMutation.mutateAsync({ id: identityId, action })
      }
      if (propertyId != null) {
        await propertyMutation.mutateAsync({ id: propertyId, action })
      }
      toast.success(String(t('accessControl.supportConsole.reviewSuccess')))
      await refreshQueues()
    } catch {
      // toast handled in mutation onError
    }
  }

  const identities = identityQ.data ?? []
  const properties = propertyQ.data ?? []
  const propertyOwners = buildPropertyOwnerSet(properties)

  const ownerListingQueue = buildOwnerListingQueue(properties, identities)
  const pendingTenantIdentity = buildTenantQueue(identities, properties)

  const historyOwnerIdentity = identities.filter((row) => {
    if (IDENTITY_PENDING.has(row.status)) return false
    const fk = trimUserFk(row.user_fk)
    return propertyOwners.has(fk) || isOwnerRole(row.user_role)
  })
  const historyTenantIdentity = identities.filter((row) => {
    if (IDENTITY_PENDING.has(row.status)) return false
    const fk = trimUserFk(row.user_fk)
    if (propertyOwners.has(fk) || isOwnerRole(row.user_role)) return false
    const role = row.user_role || 'tenant'
    if (role === 'support' || role === 'admin') return false
    return true
  })
  const historyProperty = properties.filter((row) => !PROPERTY_PENDING.has(row.status))

  const busy = identityMutation.isLoading || propertyMutation.isLoading

  if (identityQ.isLoading || propertyQ.isLoading) return <PageLoading />

  const openLabel = String(t('accessControl.supportConsole.openDocument'))
  const unavailableLabel = String(t('accessControl.supportConsole.documentUnavailable'))
  const noDocumentsLabel = String(t('accessControl.supportConsole.noDocuments'))
  const photosTitle = String(t('accessControl.supportConsole.photosTitle'))
  const noPhotosLabel = String(t('accessControl.supportConsole.noPhotos'))
  const identityDocsTitle = String(t('accessControl.supportConsole.identityDocumentsTitle'))
  const listingDocsTitle = String(t('accessControl.supportConsole.listingDocumentsTitle'))

  return (
    <div className="mx-auto max-w-6xl space-y-4 px-4 py-8">
      <h1 className="text-2xl font-bold text-imovSecondary">
        {t('accessControl.supportConsole.title')}
      </h1>
      <p className="text-sm text-gray-600">{t('accessControl.supportConsole.description')}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">
            {t('accessControl.supportConsole.pendingOwnerPropertyLabel')}
          </p>
          <p className="text-xl font-semibold">{ownerListingQueue.length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">
            {t('accessControl.supportConsole.pendingTenantLabel')}
          </p>
          <p className="text-xl font-semibold">{pendingTenantIdentity.length}</p>
        </div>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-imovSecondary">
              {t('accessControl.supportConsole.ownerListingQueueTitle')}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {t('accessControl.supportConsole.ownerListingQueueDescription')}
            </p>
          </div>
          <span className="text-xs text-gray-500">{ownerListingQueue.length}</span>
        </div>
        <div className="space-y-2">
          {ownerListingQueue.length === 0 ? (
            <p className="text-sm text-gray-500">{t('accessControl.supportConsole.noReviews')}</p>
          ) : (
            ownerListingQueue.map((item) => {
              const title =
                item.property?.property_title ||
                (item.property
                  ? t('accessControl.supportConsole.propertyRef', {
                      id: item.property.property_fk,
                    })
                  : t('accessControl.supportConsole.userRef', { id: item.ownerFk }))
              const badgeStatus =
                item.property?.status || item.identity?.status || 'property_docs_in_review'
              const badgeLabel = item.property
                ? listingStatusLabel(item.property.status)
                : identityStatusLabel(item.identity?.status || '')

              return (
                <div key={item.key} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{title}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {item.needsIdentity
                          ? t('accessControl.supportConsole.firstListingWithIdentity')
                          : t('accessControl.supportConsole.listingOnlyValidation')}
                        {' · '}
                        {t('accessControl.supportConsole.userRef', { id: item.ownerFk })}
                      </p>
                    </div>
                    <QueueBadge status={badgeStatus} label={badgeLabel} />
                  </div>

                  {item.needsIdentity ? (
                    <ReviewDocuments
                      title={identityDocsTitle}
                      documents={item.identity?.documents}
                      openLabel={openLabel}
                      unavailableLabel={unavailableLabel}
                      emptyLabel={noDocumentsLabel}
                    />
                  ) : null}

                  {item.property ? (
                    <>
                      <ReviewDocuments
                        title={listingDocsTitle}
                        documents={item.property.documents}
                        openLabel={openLabel}
                        unavailableLabel={unavailableLabel}
                        emptyLabel={noDocumentsLabel}
                      />
                      <ReviewPhotos
                        photos={item.property.photos}
                        title={photosTitle}
                        openLabel={openLabel}
                        emptyLabel={noPhotosLabel}
                      />
                    </>
                  ) : null}

                  <ReviewActions
                    disabled={busy}
                    onApprove={() => void reviewOwnerListing(item, 'approve')}
                    onRequestCorrection={() => void reviewOwnerListing(item, 'request_correction')}
                    onReject={() => void reviewOwnerListing(item, 'reject')}
                    approveLabel={String(
                      item.needsIdentity
                        ? t('accessControl.supportConsole.approveOwnerAndListing')
                        : t('accessControl.supportConsole.approve')
                    )}
                    requestCorrectionLabel={String(
                      t('accessControl.supportConsole.requestCorrection')
                    )}
                    rejectLabel={String(t('accessControl.supportConsole.reject'))}
                  />
                </div>
              )
            })
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-imovSecondary">
              {t('accessControl.supportConsole.tenantQueueTitle')}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {t('accessControl.supportConsole.tenantQueueDescription')}
            </p>
          </div>
          <span className="text-xs text-gray-500">{pendingTenantIdentity.length}</span>
        </div>
        <div className="space-y-2">
          {pendingTenantIdentity.length === 0 ? (
            <p className="text-sm text-gray-500">{t('accessControl.supportConsole.noReviews')}</p>
          ) : (
            pendingTenantIdentity.map((row) => (
              <div key={row.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t('accessControl.supportConsole.userRef', { id: row.user_fk })}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('accessControl.supportConsole.tenantIdentity')}
                      {' · '}
                      {t('accessControl.supportConsole.documentsCount', {
                        count: row.documents?.length ?? row.documents_count ?? 0,
                      })}
                    </p>
                  </div>
                  <QueueBadge status={row.status} label={identityStatusLabel(row.status)} />
                </div>
                <ReviewDocuments
                  documents={row.documents}
                  openLabel={openLabel}
                  unavailableLabel={unavailableLabel}
                  emptyLabel={noDocumentsLabel}
                />
                <ReviewActions
                  disabled={busy}
                  onApprove={() => void reviewTenantIdentity(row.id, 'approve')}
                  onRequestCorrection={() =>
                    void reviewTenantIdentity(row.id, 'request_correction')
                  }
                  onReject={() => void reviewTenantIdentity(row.id, 'reject')}
                  approveLabel={String(t('accessControl.supportConsole.approve'))}
                  requestCorrectionLabel={String(
                    t('accessControl.supportConsole.requestCorrection')
                  )}
                  rejectLabel={String(t('accessControl.supportConsole.reject'))}
                />
              </div>
            ))
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-imovSecondary">
              {t('accessControl.supportConsole.historyTitle')}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {t('accessControl.supportConsole.historyDescription')}
            </p>
          </div>
          <span className="text-xs text-gray-500">
            {historyProperty.length + historyOwnerIdentity.length + historyTenantIdentity.length}
          </span>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('accessControl.supportConsole.ownerListingHistoryTitle')}
            </h3>
            <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100">
              {historyProperty.length === 0 && historyOwnerIdentity.length === 0 ? (
                <p className="bg-gray-50 px-3 py-3 text-sm text-gray-500">
                  {t('accessControl.supportConsole.noHistory')}
                </p>
              ) : (
                <>
                  {historyProperty.map((row) => (
                    <details key={`property-history-${row.id}`} className="group bg-gray-50">
                      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-3 py-3 marker:content-none [&::-webkit-details-marker]:hidden">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {row.property_title ||
                              t('accessControl.supportConsole.propertyRef', {
                                id: row.property_fk,
                              })}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {t('accessControl.supportConsole.documentsCount', {
                              count: row.documents?.length ?? row.documents_count ?? 0,
                            })}
                            {row.approved_at || row.reviewed_at || row.rejected_at
                              ? ` · ${row.approved_at || row.reviewed_at || row.rejected_at}`
                              : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <QueueBadge
                            status={row.status}
                            label={listingStatusLabel(row.status)}
                          />
                          <AccordionChevron />
                        </div>
                      </summary>
                      <div className="border-t border-gray-100 px-3 pb-3">
                        <p className="mt-3 text-xs text-gray-500">
                          {t('accessControl.supportConsole.filesSealed')}
                        </p>
                        <ReviewDocuments
                          documents={row.documents}
                          openLabel={openLabel}
                          unavailableLabel={String(t('accessControl.supportConsole.fileLocked'))}
                          emptyLabel={noDocumentsLabel}
                          allowOpen={false}
                        />
                      </div>
                    </details>
                  ))}
                  {historyOwnerIdentity.map((row) => (
                    <details key={`owner-identity-history-${row.id}`} className="group bg-gray-50">
                      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-3 py-3 marker:content-none [&::-webkit-details-marker]:hidden">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {t('accessControl.supportConsole.userRef', { id: row.user_fk })}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {t('accessControl.supportConsole.ownerIdentity')}
                            {' · '}
                            {t('accessControl.supportConsole.documentsCount', {
                              count: row.documents?.length ?? row.documents_count ?? 0,
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <QueueBadge
                            status={row.status}
                            label={identityStatusLabel(row.status)}
                          />
                          <AccordionChevron />
                        </div>
                      </summary>
                      <div className="border-t border-gray-100 px-3 pb-3">
                        <p className="mt-3 text-xs text-gray-500">
                          {t('accessControl.supportConsole.filesSealed')}
                        </p>
                        <ReviewDocuments
                          documents={row.documents}
                          openLabel={openLabel}
                          unavailableLabel={String(t('accessControl.supportConsole.fileLocked'))}
                          emptyLabel={noDocumentsLabel}
                          allowOpen={false}
                        />
                      </div>
                    </details>
                  ))}
                </>
              )}
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('accessControl.supportConsole.tenantHistoryTitle')}
            </h3>
            <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100">
              {historyTenantIdentity.length === 0 ? (
                <p className="bg-gray-50 px-3 py-3 text-sm text-gray-500">
                  {t('accessControl.supportConsole.noHistory')}
                </p>
              ) : (
                historyTenantIdentity.map((row) => (
                  <details key={`tenant-history-${row.id}`} className="group bg-gray-50">
                    <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-3 py-3 marker:content-none [&::-webkit-details-marker]:hidden">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {t('accessControl.supportConsole.userRef', { id: row.user_fk })}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {t('accessControl.supportConsole.tenantIdentity')}
                          {' · '}
                          {t('accessControl.supportConsole.documentsCount', {
                            count: row.documents?.length ?? row.documents_count ?? 0,
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <QueueBadge status={row.status} label={identityStatusLabel(row.status)} />
                        <AccordionChevron />
                      </div>
                    </summary>
                    <div className="border-t border-gray-100 px-3 pb-3">
                      <p className="mt-3 text-xs text-gray-500">
                        {t('accessControl.supportConsole.filesSealed')}
                      </p>
                      <ReviewDocuments
                        documents={row.documents}
                        openLabel={openLabel}
                        unavailableLabel={String(t('accessControl.supportConsole.fileLocked'))}
                        emptyLabel={noDocumentsLabel}
                        allowOpen={false}
                      />
                    </div>
                  </details>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default SupportConsole
